<template>
  <WdsStack direction-column gap-lg>
    <WdsStack justify-space-between class="tw-grow">
      <WdsText large>
        <template #default>{{ $t('Milestone') }}</template>
        <template #subText>
          <span class="tw-text-base">{{ $t('Select from the list and mark as completed') }}</span>
        </template>
      </WdsText>
      <WdsSelect
        v-model="selectedOption"
        data-test="milestone-select"
        class="tw-w-[300px] tw-max-w-full"
        :placeholder="$t('Select a milestone...')"
        :list="milestoneOptions"
        :disabled="!store.isEditable"
        can-deselect
      />
    </WdsStack>
    <WdsAlert v-if="milestoneStore.error" type="error">
      {{ $t('Failed to load milestones') }}
    </WdsAlert>
    <WdsAlert v-else-if="selectedMilestone" type="info">
      {{ selectedMilestone.name }}
      <template #description>
        <div class="tw-flex tw-flex-col tw-gap-2">
          <div>{{ $t('Due date') }}: {{ selectedMilestone.details.dueDate }}</div>
          <div v-if="selectedMilestone.purchaseOrderNumber">
            {{ $t('PO Number') }}: {{ selectedMilestone.purchaseOrderNumber }}
          </div>
        </div>
      </template>
    </WdsAlert>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { WdsAlert, WdsSelect, WdsStack, WdsText } from '@worksome/wds-components'
import { usePaymentRequestStore } from './store/usePaymentRequestStore'
import { useMilestoneStore } from './store/useMilestoneStore'
import { useTranslation } from '@/composables/translation'
import { PaymentRequestTypeEnum } from './enums'

const { $t } = useTranslation()
const store = usePaymentRequestStore()
const milestoneStore = useMilestoneStore()

const milestoneOptions = computed(() => [
  ...milestoneStore.milestones.map((milestone) => ({
    value: milestone.id,
    label: milestone.name,
  })),
])

const updateSubtotal = (milestoneId: string | null) => {
  const amountInCents =
    milestoneStore.milestones.find((m) => m.id === milestoneId)?.details?.amount || 0
  const amountInFloat = Math.round(amountInCents / 100)
  store.paymentRequestRate.rate = amountInFloat
  store.paymentRequestForm.billableTime = amountInFloat ? 1 : 0
  store.paymentRequestForm.billableTotal = amountInFloat.toFixed(2)
}

const selectedOption = computed({
  get: () => {
    const milestone = milestoneStore.milestones.find(
      (m) => m.id === store.paymentRequestForm.milestoneId,
    )
    return milestone ? { value: milestone.id, label: milestone.name } : null
  },
  set: (value: { value: string | null; label: string } | string) => {
    const milestoneId = value && typeof value === 'object' ? value.value : value
    milestoneStore.selectMilestone(milestoneId || null)
    store.paymentRequestForm.milestoneId = milestoneId || null
    updateSubtotal(milestoneId)
  },
})

const selectedMilestone = computed(() => milestoneStore.selectedMilestone)

// Initialize subtotal when component mounts if there's a selected milestone
watch(
  () => store.paymentRequestType,
  (newType) => {
    if (newType === PaymentRequestTypeEnum.MILESTONE && store.paymentRequestForm.milestoneId) {
      updateSubtotal(store.paymentRequestForm.milestoneId)
    }
  },
  { immediate: true },
)
</script>
